import type {
  BindCheckoutPolicyRequest,
  CheckoutSurface,
  PaymentSession,
  ProblemJson,
  StartCheckoutRequest,
  VerifyPayerResponse,
  ZippyPayError,
} from '../types';
import { ZippyPayError as ZippyPayErrorClass } from '../types';
import { ZIPPY_API_PATH_PREFIX } from '../config/environments';
import { normalizeBaseUrl, normalizePaymentId, normalizePayerQuery } from '../utils';

export type ApiClientConfig = {
  baseUrl: string;
  sessionId: string;
  clientToken: string;
};

export class PaymentSessionClient {
  private readonly baseUrl: string;
  private readonly sessionId: string;
  private readonly clientToken: string;

  constructor(config: ApiClientConfig) {
    this.baseUrl = normalizeBaseUrl(config.baseUrl);
    this.sessionId = config.sessionId;
    this.clientToken = config.clientToken;
  }

  async getSession(): Promise<PaymentSession> {
    return this.request<PaymentSession>('GET', this.sessionPath());
  }

  async bindCheckoutPolicy(body: BindCheckoutPolicyRequest): Promise<PaymentSession> {
    return this.request<PaymentSession>('POST', `${this.sessionPath()}/checkout/bind-policy`, body);
  }

  async verifyPayer(query: string): Promise<VerifyPayerResponse> {
    return this.request<VerifyPayerResponse>('POST', `${this.sessionPath()}/payer/verify`, {
      query: normalizePayerQuery(query),
    });
  }

  async attachPayer(paymentId: string): Promise<PaymentSession> {
    return this.request<PaymentSession>('POST', `${this.sessionPath()}/payer`, {
      paymentId: normalizePaymentId(paymentId),
    });
  }

  async startCheckout(
    method: StartCheckoutRequest['method'],
    surface: CheckoutSurface,
  ): Promise<PaymentSession> {
    const body: StartCheckoutRequest = { method, surface };
    return this.request<PaymentSession>('POST', `${this.sessionPath()}/checkout/start`, body);
  }

  startQrCheckout(surface: CheckoutSurface): Promise<PaymentSession> {
    return this.startCheckout('QR', surface);
  }

  startMobileAppCheckout(surface: CheckoutSurface): Promise<PaymentSession> {
    return this.startCheckout('MOBILE_APP', surface);
  }

  startManualCheckout(surface: CheckoutSurface): Promise<PaymentSession> {
    return this.startCheckout('MANUAL', surface);
  }

  private sessionPath(): string {
    return `${ZIPPY_API_PATH_PREFIX}/payment-sessions/${this.sessionId}`;
  }

  private headers(): Record<string, string> {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Payment-Session-Token': this.clientToken,
    };
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: this.headers(),
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.ok) {
      return (await response.json()) as T;
    }

    throw await this.parseError(response);
  }

  private async parseError(response: Response): Promise<ZippyPayError> {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/problem+json')) {
      const problem = (await response.json()) as ProblemJson;
      return new ZippyPayErrorClass({ ...problem, status: problem.status ?? response.status });
    }

    return new ZippyPayErrorClass({
      status: response.status,
      code: 'unknown',
      title: 'Request failed',
      detail: response.statusText || 'Request failed',
    });
  }
}

export async function attachPayerWithRetry(
  client: PaymentSessionClient,
  paymentId: string,
): Promise<PaymentSession> {
  try {
    return await client.attachPayer(paymentId);
  } catch (error) {
    if (!(error instanceof ZippyPayErrorClass) || error.code !== 'payment-session.payer-attached') {
      throw error;
    }
    const session = await client.getSession();
    if (session.payer?.paymentId === normalizePaymentId(paymentId)) {
      return session;
    }
    throw error;
  }
}
